import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { NgxSpinnerService } from 'ngx-spinner';
import { splitIntoPages } from './docx-conversion-pipeline';
import { DEFAULT_DOCX_FONT_FAMILY } from './docx-fidelity-transform';
import { ConversionContext, finalizeConversionHtml, prepareConversionContext } from './docx-conversion-pipeline';
import { PageGeometry } from './docx-xml-model';
import { FIELD_TYPE_DEFS, FieldType, FieldTypeDef, TemplateField, hasOptionsList, supportsMinMaxLength, supportsMinMaxValue, supportsValidationPattern } from './template-field.model';

@Component({
  selector: 'app-docx-to-html-converter',
  templateUrl: './docx-to-html-converter.component.html',
  styleUrls: ['./docx-to-html-converter.component.scss'],
  standalone: false,
})
export class DocxToHtmlConverterComponent implements OnInit, OnDestroy {
  private static readonly EDITABLE_CANVAS_STYLE_ID = 'docx-editable-canvas-styles';

  fileName = '';
  isConverting = false;
  progressPercent = 0;
  pageCount = 0;
  errorMessage = '';
  warnings: string[] = [];
  warningsExpanded = false;
  previewDocument: SafeHtml | null = null;
  previewEngine: 'mammoth' | 'docx-preview' = 'mammoth';
  docxPreviewDocument: SafeHtml | null = null;
  docxPreviewDurationMs: number | null = null;
  docxPreviewError: string | null = null;
  isRenderingDocxPreview = false;

  readonly fieldTypeDefs: FieldTypeDef[] = FIELD_TYPE_DEFS;
  fields: TemplateField[] = [];
  activeFieldId: string | null = null;
  popupPosition: { top: number; left: number } | null = null;
  advancedExpanded = false;

  readonly environmentOptions = ['Development', 'QA', 'UAT', 'Production'];
  accountId = '';
  environment = '';
  uploadStatusMessage = '';

  @ViewChild('editableRoot') editableRootRef?: ElementRef<HTMLDivElement>;
  @ViewChild('fieldPopup') fieldPopupRef?: ElementRef<HTMLDivElement>;

  private fieldIdCounter = 0;
  private syncTimer: ReturnType<typeof setTimeout> | null = null;

  private selectedFile: File | null = null;
  private documentHtml = '';
  private listFormatCss = '';
  private worker: Worker | null = null;
  private progressTimer: ReturnType<typeof setInterval> | null = null;
  private pageGeometry: PageGeometry | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private spinner: NgxSpinnerService,
    private cdr: ChangeDetectorRef
  ) {
    this.spinner.hide();
  }

  ngOnInit(): void {
    this.spinner.hide();
  }

  ngOnDestroy(): void {
    this.terminateWorker();
    this.clearProgressTimer();
    document.getElementById(DocxToHtmlConverterComponent.EDITABLE_CANVAS_STYLE_ID)?.remove();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file) {
      return;
    }

    if (!/\.docx$/i.test(file.name)) {
      this.errorMessage = 'Please select a .docx file.';
      return;
    }

    this.convertFile(file);
  }

  private async convertFile(file: File): Promise<void> {
    this.errorMessage = '';
    this.warnings = [];
    this.warningsExpanded = false;
    this.previewDocument = null;
    this.documentHtml = '';
    this.pageCount = 0;
    this.fileName = file.name;
    this.selectedFile = file;
    this.previewEngine = 'mammoth';
    this.docxPreviewDocument = null;
    this.docxPreviewDurationMs = null;
    this.docxPreviewError = null;
    this.isConverting = true;
    this.exitFieldEditingState();
    this.startProgress();

    try {
      const arrayBuffer = await file.arrayBuffer();

      const context = await prepareConversionContext(arrayBuffer);

      if (typeof Worker !== 'undefined') {
        await this.convertOnWorker(arrayBuffer, context);
      } else {
        const mammothModule: any = await import('mammoth');
        const mammoth = mammothModule.convertToHtml ? mammothModule : mammothModule.default;
        const { runMammothConversion } = await import('./docx-conversion-pipeline');
        const result = await runMammothConversion(arrayBuffer, mammoth, context);
        this.applyResult(result.html, result.warnings, context);
        this.finishConversion();
      }
    } catch (error) {
      console.error('[docx2html] Error converting docx to html:', error);
      this.errorMessage = 'Failed to convert the document. Please make sure it is a valid .docx file.';
      this.finishConversion();
      this.cdr.detectChanges();
    }
  }

  private convertOnWorker(arrayBuffer: ArrayBuffer, context: ConversionContext): Promise<void> {
    return new Promise((resolve) => {
      this.terminateWorker();
      this.worker = new Worker(new URL('./docx-to-html.worker', import.meta.url));

      this.worker.onmessage = ({ data }) => {
        if (data.type === 'success') {
          this.applyResult(data.html, data.warnings, context);
        } else {
          console.error('[docx2html] worker reported error:', data?.message);
          this.errorMessage = 'Failed to convert the document. Please make sure it is a valid .docx file.';
        }
        this.finishConversion();
        this.terminateWorker();
        this.cdr.detectChanges();
        resolve();
      };

      this.worker.onerror = (event) => {
        console.error('[docx2html] worker onerror:', event.message, event.filename, event.lineno);
        this.errorMessage = 'Failed to convert the document. Please make sure it is a valid .docx file.';
        this.finishConversion();
        this.terminateWorker();
        this.cdr.detectChanges();
        resolve();
      };

      this.worker.postMessage(
        {
          arrayBuffer,
          styleMap: context.styleMap,
          paragraphSpacing: context.paragraphSpacing,
          runFormattingByStyleId: context.runFormattingByStyleId,
          runIsLastRenderedPageBreakMarker: context.runIsLastRenderedPageBreakMarker,
          paragraphHasSectionPageBreak: context.paragraphHasSectionPageBreak,
          paragraphNumberLabel: context.paragraphNumberLabel,
          paragraphNumberIndent: context.paragraphNumberIndent,
          imageDimensions: context.imageDimensions,
          runParagraphStyleId: context.runParagraphStyleId,
          runFontByStyleId: context.runFontByStyleId,
          runFontDocDefault: context.runFontDocDefault,
        },
        [arrayBuffer]
      );
    });
  }

  private applyResult(rawHtmlFromMammoth: string, warnings: string[], context: ConversionContext): void {
    const rawBodyHtml = finalizeConversionHtml(rawHtmlFromMammoth, context.tables);
    const xmlPages = splitIntoPages(rawBodyHtml);

    const hasXmlSignals =
      context.explicitPageBreakCount > 0 ||
      context.runIsLastRenderedPageBreakMarker.some(Boolean) ||
      context.paragraphHasSectionPageBreak.some(Boolean);

    // splitIntoPages only sees explicit XML page-break signals, which can be sparse across a
    // document with dozens of real pages. Every XML-delimited chunk is independently re-measured
    // by rendered DOM height and subdivided further so the page count matches what will render.
    const pages: string[] = [];
    let anyChunkSubdivided = false;
    xmlPages.forEach((chunkHtml) => {
      const subPages = this.paginateByDomHeight(chunkHtml, context.pageGeometry);
      if (subPages.length > 1) {
        anyChunkSubdivided = true;
      }
      pages.push(...subPages);
    });

    if (anyChunkSubdivided) {
      context.diagnostics.push(
        `[diagnostic] ${xmlPages.length} XML-delimited section(s) were further subdivided by DOM height measurement into ${pages.length} total page(s) - explicit page-break signals in the source are sparse relative to the actual content length`
      );
    } else if (!hasXmlSignals && pages.length > 1) {
      context.diagnostics.push(
        `[diagnostic] no XML page-break signals found — used DOM height measurement to split into ${pages.length} pages`
      );
    }

    this.pageGeometry = context.pageGeometry;
    this.pageCount = pages.length;
    this.listFormatCss = context.listFormatCss;
    this.documentHtml = this.buildFullHtmlDocument(pages, context.listFormatCss);
    this.previewDocument = this.sanitizer.bypassSecurityTrustHtml(this.documentHtml);
    this.warnings = [...context.diagnostics, ...warnings];
    this.schedulePopulateCanvas();
  }

  private paginateByDomHeight(bodyHtml: string, pageGeometry: PageGeometry): string[] {
    const TWIPS_PER_PX = 96 / 1440;
    const usableHeightPx = (pageGeometry.pageHeightTwips - pageGeometry.marginTopTwips - pageGeometry.marginBottomTwips) * TWIPS_PER_PX;
    const pageWidthPx = (pageGeometry.pageWidthTwips - pageGeometry.marginLeftTwips - pageGeometry.marginRightTwips) * TWIPS_PER_PX;

    const container = document.createElement('div');
    container.style.cssText = `
      position: absolute;
      left: -99999px;
      top: 0;
      width: ${pageWidthPx}px;
      visibility: hidden;
      font-family: Cambria, Georgia, 'Times New Roman', serif;
      font-size: 12pt;
      line-height: 1.5;
      color: #222;
    `;
    document.body.appendChild(container);

    try {
      const doc = new DOMParser().parseFromString(`<div>${bodyHtml}</div>`, 'text/html');
      const root = doc.body.firstElementChild!;
      const topLevelChildren = Array.from(root.children);

      const pages: string[] = [];
      let currentPageChildren: Element[] = [];
      let runningHeightPx = 0;

      for (const child of topLevelChildren) {
        const clone = child.cloneNode(true) as Element;
        container.appendChild(clone);
        const childHeightPx = clone.getBoundingClientRect().height || (clone as HTMLElement).scrollHeight;
        container.removeChild(clone);

        if (runningHeightPx + childHeightPx > usableHeightPx && currentPageChildren.length > 0) {
          pages.push(currentPageChildren.map((el) => el.outerHTML).join('\n'));
          currentPageChildren = [];
          runningHeightPx = 0;
        }

        currentPageChildren.push(child);
        runningHeightPx += childHeightPx;
      }

      if (currentPageChildren.length > 0) {
        pages.push(currentPageChildren.map((el) => el.outerHTML).join('\n'));
      }

      return pages.length > 0 ? pages : [bodyHtml];
    } finally {
      document.body.removeChild(container);
    }
  }

  private buildDocumentStyles(pageGeometry: PageGeometry, rootSelector = 'body'): string {
    const TWIPS_TO_MM = 1 / 56.693;
    const pageWidthMm = Math.round(pageGeometry.pageWidthTwips * TWIPS_TO_MM);
    const pageHeightMm = Math.round(pageGeometry.pageHeightTwips * TWIPS_TO_MM);
    const marginTopMm = Math.round(pageGeometry.marginTopTwips * TWIPS_TO_MM);
    const marginBottomMm = Math.round(pageGeometry.marginBottomTwips * TWIPS_TO_MM);
    const marginLeftMm = Math.round(pageGeometry.marginLeftTwips * TWIPS_TO_MM);
    const marginRightMm = Math.round(pageGeometry.marginRightTwips * TWIPS_TO_MM);

    return `
  ${rootSelector} { font-family: ${DEFAULT_DOCX_FONT_FAMILY}, Georgia, 'Times New Roman', serif; line-height: 1.5; color: #222; margin: 0; padding: 20px; background: #f0f0f0; }
  /* Force all document text/list-markers to a single black/serif look regardless of the source
     run's own inline color/font. Field chips (.tf-field) are excluded so FIELD_CHIP_STYLES'
     per-type colors aren't overridden. */
  ${rootSelector} *:not(.tf-field):not(.tf-field *),
  ${rootSelector} *:not(.tf-field):not(.tf-field *)::before,
  ${rootSelector} *:not(.tf-field):not(.tf-field *)::after {
    color: #000000 !important;
    font-family: ${DEFAULT_DOCX_FONT_FAMILY}, Georgia, 'Times New Roman', serif !important;
  }
  .docx-document {
    position: relative;
    background: #fff;
    width: ${pageWidthMm}mm;
    min-height: ${pageHeightMm}mm;
    margin: 0 auto 24px;
    padding: ${marginTopMm}mm ${marginRightMm}mm ${marginBottomMm}mm ${marginLeftMm}mm;
    box-sizing: border-box;
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.15);
  }
  .docx-document:last-of-type { margin-bottom: 0; }
  .docx-page-footer {
    position: absolute;
    left: 0;
    right: 0;
    bottom: ${Math.max(4, Math.round(marginBottomMm / 2))}mm;
    text-align: center;
    font-size: 9pt;
    color: #666;
  }
  p { margin: 0 0 0.5em; }
  ol, ul { margin: 0.25em 0; padding-left: 1.75em; }
  li { page-break-inside: avoid; }
  table { border-collapse: collapse; width: 100%; margin: 1rem 0; page-break-inside: avoid; }
  table, th, td { border: 1px solid #ccc; }
  th, td { padding: 0.5rem; vertical-align: top; }
  img { max-width: 100%; height: auto; }
  .term-definition { font-style: italic; }
  @media print {
    body { background: #fff; padding: 0; }
    @page { size: ${pageWidthMm}mm ${pageHeightMm}mm; margin: ${marginTopMm}mm ${marginRightMm}mm ${marginBottomMm}mm ${marginLeftMm}mm; }
    .docx-document { box-shadow: none; margin: 0; padding: 0; width: auto; min-height: 0; page-break-after: always; }
    .docx-document:last-of-type { page-break-after: auto; }
    .docx-page-footer { position: static; margin-top: 12px; }
    p, table, li { page-break-inside: avoid; }
  }`;
  }

  private buildFullHtmlDocument(pages: string[], listFormatCss: string): string {
    const totalPages = pages.length;
    const pagesHtml = pages
      .map(
        (pageHtml, i) =>
          `<div class="docx-document">\n${pageHtml}\n<div class="docx-page-footer">Page ${i + 1} of ${totalPages}</div>\n</div>`
      )
      .join('\n');
    return this.wrapHtmlDocument(pagesHtml, listFormatCss);
  }

  private wrapHtmlDocument(pagesHtml: string, listFormatCss: string): string {
    const title = this.fileName.replace(/[<>&]/g, '').replace(/\.docx$/i, '') || 'Converted document';
    return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>${title}</title>
<style>${this.buildDocumentStyles(this.resolveGeometry())}
${listFormatCss}</style>
</head>
<body>
${pagesHtml}
</body>
</html>`;
  }

  private resolveGeometry(): PageGeometry {
    return (
      this.pageGeometry ?? {
        pageWidthTwips: 11906,
        pageHeightTwips: 16838,
        marginTopTwips: 1134,
        marginBottomTwips: 1134,
        marginLeftTwips: 1134,
        marginRightTwips: 1134,
      }
    );
  }

  private startProgress(): void {
    this.progressPercent = 0;
    this.clearProgressTimer();
    this.progressTimer = setInterval(() => {
      if (this.progressPercent < 90) {
        this.progressPercent += Math.max(1, Math.round((90 - this.progressPercent) / 10));
        this.cdr.detectChanges();
      }
    }, 200);
  }

  private finishConversion(): void {
    this.clearProgressTimer();
    this.progressPercent = 100;
    this.isConverting = false;
  }

  private clearProgressTimer(): void {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  }

  private terminateWorker(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }

  toggleWarnings(): void {
    this.warningsExpanded = !this.warningsExpanded;
  }

  setPreviewEngine(engine: 'mammoth' | 'docx-preview'): void {
    if (engine === 'docx-preview' && !this.docxPreviewDocument && !this.isRenderingDocxPreview) {
      this.runDocxPreviewComparison();
      return;
    }
    const switchingToMammoth = engine === 'mammoth' && this.previewEngine !== 'mammoth';
    this.previewEngine = engine;
    if (switchingToMammoth) {
      this.schedulePopulateCanvas();
    }
  }

  get fieldsEditingEnabled(): boolean {
    return !!this.previewDocument && this.previewEngine === 'mammoth';
  }

  private async runDocxPreviewComparison(): Promise<void> {
    if (!this.selectedFile || this.isRenderingDocxPreview) {
      return;
    }

    this.isRenderingDocxPreview = true;
    this.docxPreviewError = null;
    this.cdr.detectChanges();

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-99999px';
    container.style.top = '0';
    document.body.appendChild(container);

    try {
      const { renderAsync } = await import('docx-preview');
      const arrayBuffer = await this.selectedFile.arrayBuffer();

      const start = performance.now();
      await renderAsync(arrayBuffer, container, undefined, {
        inWrapper: true,
        ignoreLastRenderedPageBreak: false,
      });
      this.docxPreviewDurationMs = Math.round(performance.now() - start);

      const combinedHtml = `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><title>docx-preview comparison</title></head>
<body>${container.innerHTML}</body>
</html>`;
      this.docxPreviewDocument = this.sanitizer.bypassSecurityTrustHtml(combinedHtml);
      this.previewEngine = 'docx-preview';
    } catch (error) {
      console.error('[docx2html] docx-preview comparison failed:', error);
      this.docxPreviewError = 'docx-preview failed to render this file.';
      this.previewEngine = 'docx-preview';
    } finally {
      document.body.removeChild(container);
      this.isRenderingDocxPreview = false;
      this.cdr.detectChanges();
    }
  }

  // Only its button in the template is commented out in favor of "Confirm Upload"; restore by
  // uncommenting the button.
  downloadHtml(): void {
    this.syncDocumentFromEditor();

    if (!this.documentHtml) {
      return;
    }

    const blob = new Blob([this.documentHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.fileName.replace(/\.docx$/i, '') || 'converted'}.html`;
    link.click();
    URL.revokeObjectURL(url);
  }

  // Written imperatively onto the DOM element, not just onto `accountId`: when the sanitized
  // value equals what accountId already held, Angular's [ngModel] binding sees no change and
  // skips writeValue(), leaving the browser's own uncapped keystrokes in the input.
  onAccountIdInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const sanitized = input.value.replace(/\D/g, '').slice(0, 4);
    this.accountId = sanitized;
    input.value = sanitized;
  }

  // Endpoint isn't decided yet - this only assembles the payload so the real POST call can be
  // dropped in later.
  confirmUpload(): void {
    this.syncDocumentFromEditor();

    if (!this.documentHtml) {
      return;
    }

    const htmlFileName = `${this.fileName.replace(/\.docx$/i, '') || 'converted'}.html`;
    const htmlFile = new File([this.documentHtml], htmlFileName, { type: 'text/html' });

    const formData = new FormData();
    formData.append('accountId', this.accountId);
    formData.append('environment', this.environment);
    formData.append('fields', JSON.stringify(this.fields));
    formData.append('file', htmlFile);

    // TODO: once the upload endpoint is available, replace this with the real call, e.g.:
    // this.http.post('/api/templates/upload', formData).subscribe(...)
    console.log('[docx2html] Confirm Upload - payload prepared (API integration pending):', {
      accountId: this.accountId,
      environment: this.environment,
      fields: this.fields,
      fileName: htmlFile.name,
      formData,
    });

    this.uploadStatusMessage = 'Upload payload prepared - API integration pending. See browser console for details.';
  }

  reset(): void {
    this.terminateWorker();
    this.clearProgressTimer();
    this.exitFieldEditingState();
    this.fileName = '';
    this.errorMessage = '';
    this.warnings = [];
    this.warningsExpanded = false;
    this.previewDocument = null;
    this.documentHtml = '';
    this.listFormatCss = '';
    this.progressPercent = 0;
    this.pageCount = 0;
    this.isConverting = false;
    this.selectedFile = null;
    this.pageGeometry = null;
    this.previewEngine = 'mammoth';
    this.docxPreviewDocument = null;
    this.docxPreviewDurationMs = null;
    this.docxPreviewError = null;
    this.accountId = '';
    this.environment = '';
    this.uploadStatusMessage = '';
  }

  private schedulePopulateCanvas(): void {
    setTimeout(() => {
      const root = this.editableRootRef?.nativeElement;
      if (!root || !this.documentHtml) {
        return;
      }
      this.updateEditableCanvasStyles();
      const doc = new DOMParser().parseFromString(this.documentHtml, 'text/html');
      root.innerHTML = doc.body.innerHTML;
      this.hydrateFieldListeners();
    });
  }

  // .tf-field chips are inserted via plain DOM APIs, not Angular's template renderer, so they
  // never get the `_ngcontent-*` attribute Angular's emulated encapsulation relies on - a rule
  // like `.tf-field-editor` in this component's own <style> block would silently never match
  // them. Defined here instead, in a <style> tag appended directly to <head>.
  private static readonly FIELD_CHIP_STYLES = `
  .tf-field {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    margin: 0 2px;
    border-radius: 6px;
    font-size: 0.85em;
    font-weight: 600;
    white-space: nowrap;
    vertical-align: baseline;
  }
  .tf-field-editor {
    background: var(--dx-primary-tint);
    color: var(--dx-primary-dark);
    border: 1px solid var(--dx-primary-border);
    cursor: pointer;
  }
  /* Fixed per-field-type colors so dropped fields are distinguishable at a glance. */
  .tf-field-editor.tf-field-text { background: #ebdeff; color: #5b21b6; border-color: #a079df; }
  .tf-field-editor.tf-field-number { background: #e8f5e9; color: #2e7d32; border-color: #a5d6a7; }
  .tf-field-editor.tf-field-date { background: #e3f2fd; color: #1565c0; border-color: #90caf9;  }
  .tf-field-editor.tf-field-dropdown { background: #f3e5f5; color: #6a1b9a; border-color: #ce93d8; }
  .tf-field-editor.tf-field-checkbox { background: #e0f7fa; color: #00838f; border-color: #80deea; }
  .tf-field-editor.tf-field-signature { background: #fce4ec; color: #ad1457; border-color: #f48fb1; }
  .tf-field-editor.tf-field-radio { background: #e8eaf6; color: #283593; border-color: #9fa8da; }
  .tf-field-remove {
    font-size: 1.1em;
    margin-left: 2px;
    border-radius: 50%;
    cursor: pointer;
  }
  .tf-field-remove:hover {
    background: rgba(0, 0, 0, 0.1);
  }`;

  // Reuses the same stylesheet the read-only iframe/downloaded HTML renders with, scoped onto
  // .editable-canvas via @scope so the page look is pixel-identical while editing.
  private updateEditableCanvasStyles(): void {
    let tag = document.getElementById(DocxToHtmlConverterComponent.EDITABLE_CANVAS_STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = DocxToHtmlConverterComponent.EDITABLE_CANVAS_STYLE_ID;
      document.head.appendChild(tag);
    }
    const scopedDocumentStyles = this.buildDocumentStyles(this.resolveGeometry(), ':scope');
    tag.textContent = `@scope (.editable-canvas) {\n${scopedDocumentStyles}\n${this.listFormatCss}\n${DocxToHtmlConverterComponent.FIELD_CHIP_STYLES}\n}`;
  }

  private exitFieldEditingState(): void {
    this.fields = [];
    this.activeFieldId = null;
    this.popupPosition = null;
    this.advancedExpanded = false;
    this.fieldIdCounter = 0;
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
  }

  onCanvasInput(): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }
    this.syncTimer = setTimeout(() => this.syncDocumentFromEditor(), 300);
  }

  private syncDocumentFromEditor(): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    const root = this.editableRootRef?.nativeElement;
    if (!root) {
      return;
    }
    const exportableHtml = this.stripEditorOnlyMarkup(root.innerHTML);
    this.documentHtml = this.wrapHtmlDocument(exportableHtml, this.listFormatCss);
    this.previewDocument = this.sanitizer.bypassSecurityTrustHtml(this.documentHtml);
  }

  // The "dropped" look exists only to differentiate a placed field while editing and must never
  // reach the exported HTML, so it's stripped before documentHtml is rebuilt.
  private stripEditorOnlyMarkup(html: string): string {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    doc.querySelectorAll('.tf-field-editor').forEach((el) => el.classList.remove('tf-field-editor'));
    return doc.body.firstElementChild?.innerHTML ?? html;
  }

  onToolbarDragStart(event: DragEvent, def: FieldTypeDef): void {
    if (!this.fieldsEditingEnabled || !event.dataTransfer) {
      event.preventDefault();
      return;
    }
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData('application/x-field-type', def.type);
  }

  onCanvasDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onCanvasDrop(event: DragEvent): void {
    event.preventDefault();
    const dataTransfer = event.dataTransfer;
    if (!dataTransfer) {
      return;
    }

    const range = this.getDropRange(event.clientX, event.clientY);
    if (!range) {
      return;
    }

    // A drop moves/adds a field elsewhere, so a popup anchored to its old position would go stale.
    this.activeFieldId = null;
    this.popupPosition = null;

    const existingFieldId = dataTransfer.getData('application/x-field-id');
    if (existingFieldId) {
      const root = this.editableRootRef?.nativeElement;
      const span = root?.querySelector<HTMLElement>(`[data-field-id="${CSS.escape(existingFieldId)}"]`);
      if (span) {
        span.remove();
        range.insertNode(span);
      }
      this.syncDocumentFromEditor();
      return;
    }

    const fieldType = dataTransfer.getData('application/x-field-type') as FieldType;
    const def = this.fieldTypeDefs.find((d) => d.type === fieldType);
    if (!def) {
      return;
    }

    const field = this.createField(def);
    this.fields.push(field);
    const el = this.createFieldElement(field);
    range.insertNode(el);
    this.syncDocumentFromEditor();
  }

  private getDropRange(clientX: number, clientY: number): Range | null {
    const docWithCaretRange = document as Document & {
      caretRangeFromPoint?: (x: number, y: number) => Range | null;
      caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    };

    if (docWithCaretRange.caretRangeFromPoint) {
      return docWithCaretRange.caretRangeFromPoint(clientX, clientY);
    }
    if (docWithCaretRange.caretPositionFromPoint) {
      const pos = docWithCaretRange.caretPositionFromPoint(clientX, clientY);
      if (!pos) {
        return null;
      }
      const range = document.createRange();
      range.setStart(pos.offsetNode, pos.offset);
      range.collapse(true);
      return range;
    }
    return null;
  }

  private createField(def: FieldTypeDef): TemplateField {
    const id = `field_${def.type}_${++this.fieldIdCounter}`;
    return {
      id,
      type: def.type,
      label: def.label,
      variableName: this.slugify(`${def.type}_${this.fieldIdCounter}`),
      required: false,
      placeholder: '',
      options: hasOptionsList(def.type) ? ['Option 1', 'Option 2'] : undefined,
    };
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'field';
  }

  private createFieldElement(field: TemplateField): HTMLElement {
    const span = document.createElement('span');
    // tf-field-editor is the "dropped" visual state, stripped before export (see
    // stripEditorOnlyMarkup). No type icon here either, to keep the placed chip clean.
    span.className = `tf-field tf-field-editor tf-field-${field.type}`;
    span.setAttribute('contenteditable', 'false');
    span.setAttribute('draggable', 'true');
    this.applyFieldAttributes(span, field);

    const labelText = document.createElement('span');
    labelText.className = 'tf-field-label-text';
    labelText.textContent = field.label;
    span.appendChild(labelText);

    const remove = document.createElement('i');
    remove.className = 'bx bx-x tf-field-remove';
    remove.addEventListener('click', (event) => {
      event.stopPropagation();
      event.preventDefault();
      this.removeField(field.id);
    });
    span.appendChild(remove);

    span.addEventListener('dragstart', (event) => {
      event.dataTransfer?.setData('application/x-field-id', field.id);
      event.dataTransfer!.effectAllowed = 'move';
    });

    return span;
  }

  private applyFieldAttributes(span: HTMLElement, field: TemplateField): void {
    span.setAttribute('data-field-id', field.id);
    span.setAttribute('data-field-type', field.type);
    span.setAttribute('data-field-label', field.label);
    span.setAttribute('data-field-variable', field.variableName);
    span.setAttribute('data-field-required', String(field.required));
    span.setAttribute('data-field-placeholder', field.placeholder ?? '');
    span.setAttribute('data-field-min', field.minValue !== undefined ? String(field.minValue) : '');
    span.setAttribute('data-field-max', field.maxValue !== undefined ? String(field.maxValue) : '');
    span.setAttribute('data-field-minlength', field.minLength !== undefined ? String(field.minLength) : '');
    span.setAttribute('data-field-maxlength', field.maxLength !== undefined ? String(field.maxLength) : '');
    span.setAttribute('data-field-default', field.defaultValue ?? '');
    span.setAttribute('data-field-pattern', field.validationPattern ?? '');
    span.setAttribute('data-field-options', field.options ? JSON.stringify(field.options) : '');
  }

  private hydrateFieldListeners(): void {
    const root = this.editableRootRef?.nativeElement;
    if (!root) {
      return;
    }
    const spans = Array.from(root.querySelectorAll<HTMLElement>('.tf-field'));
    this.fields = spans.map((span) => this.readFieldFromSpan(span));

    spans.forEach((span) => {
      // documentHtml never carries this class (stripped on export), so re-add it whenever fields
      // are re-parsed into the live editor DOM.
      span.classList.add('tf-field-editor');

      const removeIcon = span.querySelector<HTMLElement>('.tf-field-remove');
      removeIcon?.addEventListener('click', (event) => {
        event.stopPropagation();
        event.preventDefault();
        const id = span.getAttribute('data-field-id');
        if (id) {
          this.removeField(id);
        }
      });
      span.addEventListener('dragstart', (event) => {
        const id = span.getAttribute('data-field-id');
        if (id) {
          event.dataTransfer?.setData('application/x-field-id', id);
          event.dataTransfer!.effectAllowed = 'move';
        }
      });
    });

    const maxCounter = this.fields.reduce((max, f) => {
      const match = /_(\d+)$/.exec(f.id);
      return match ? Math.max(max, Number(match[1])) : max;
    }, 0);
    this.fieldIdCounter = Math.max(this.fieldIdCounter, maxCounter);
  }

  private readFieldFromSpan(span: HTMLElement): TemplateField {
    const readNumber = (attr: string): number | undefined => {
      const raw = span.getAttribute(attr);
      return raw ? Number(raw) : undefined;
    };
    const readOptions = (): string[] | undefined => {
      const raw = span.getAttribute('data-field-options');
      if (!raw) {
        return undefined;
      }
      try {
        return JSON.parse(raw);
      } catch {
        return undefined;
      }
    };

    return {
      id: span.getAttribute('data-field-id') || '',
      type: (span.getAttribute('data-field-type') as FieldType) || 'text',
      label: span.getAttribute('data-field-label') || '',
      variableName: span.getAttribute('data-field-variable') || '',
      required: span.getAttribute('data-field-required') === 'true',
      placeholder: span.getAttribute('data-field-placeholder') || '',
      minValue: readNumber('data-field-min'),
      maxValue: readNumber('data-field-max'),
      minLength: readNumber('data-field-minlength'),
      maxLength: readNumber('data-field-maxlength'),
      defaultValue: span.getAttribute('data-field-default') || undefined,
      validationPattern: span.getAttribute('data-field-pattern') || undefined,
      options: readOptions(),
    };
  }

  onCanvasClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const fieldEl = target.closest<HTMLElement>('.tf-field');
    if (!fieldEl || target.classList.contains('tf-field-remove')) {
      this.activeFieldId = null;
      this.popupPosition = null;
      return;
    }

    const id = fieldEl.getAttribute('data-field-id');
    if (!id) {
      return;
    }
    this.activeFieldId = id;
    this.advancedExpanded = false;

    const rect = fieldEl.getBoundingClientRect();
    const popupWidth = 300;
    // Clamp so the popup's max-height always fits within the viewport.
    const popupMaxHeight = Math.min(480, window.innerHeight - 32);
    const left = Math.min(rect.left, window.innerWidth - popupWidth - 16);
    const top = Math.min(rect.bottom + 6, window.innerHeight - popupMaxHeight - 16);
    this.popupPosition = { top: Math.max(8, top), left: Math.max(8, left) };
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.activeFieldId) {
      return;
    }
    const target = event.target as HTMLElement;
    const popupEl = this.fieldPopupRef?.nativeElement;
    if (popupEl && popupEl.contains(target)) {
      return;
    }
    if (target.closest('.tf-field')) {
      return;
    }
    this.activeFieldId = null;
    this.popupPosition = null;
  }

  get selectedField(): TemplateField | null {
    return this.fields.find((f) => f.id === this.activeFieldId) ?? null;
  }

  fieldSupportsOptions(field: TemplateField): boolean {
    return hasOptionsList(field.type);
  }

  fieldSupportsMinMaxValue(field: TemplateField): boolean {
    return supportsMinMaxValue(field.type);
  }

  fieldSupportsMinMaxLength(field: TemplateField): boolean {
    return supportsMinMaxLength(field.type);
  }

  fieldSupportsPattern(field: TemplateField): boolean {
    return supportsValidationPattern(field.type);
  }

  toggleAdvanced(): void {
    this.advancedExpanded = !this.advancedExpanded;
  }

  updateField(patch: Partial<TemplateField>): void {
    const field = this.selectedField;
    if (!field) {
      return;
    }
    Object.assign(field, patch);

    const root = this.editableRootRef?.nativeElement;
    const span = root?.querySelector<HTMLElement>(`[data-field-id="${CSS.escape(field.id)}"]`);
    if (span) {
      this.applyFieldAttributes(span, field);
      const labelText = span.querySelector<HTMLElement>('.tf-field-label-text');
      if (labelText) {
        labelText.textContent = field.label;
      }
    }

    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }
    this.syncTimer = setTimeout(() => this.syncDocumentFromEditor(), 300);
  }

  addOption(): void {
    const field = this.selectedField;
    if (!field) {
      return;
    }
    const options = field.options ? [...field.options] : [];
    options.push(`Option ${options.length + 1}`);
    this.updateField({ options });
  }

  updateOption(index: number, value: string): void {
    const field = this.selectedField;
    if (!field || !field.options) {
      return;
    }
    const options = [...field.options];
    options[index] = value;
    this.updateField({ options });
  }

  removeOption(index: number): void {
    const field = this.selectedField;
    if (!field || !field.options) {
      return;
    }
    const options = field.options.filter((_, i) => i !== index);
    this.updateField({ options });
  }

  removeField(id: string): void {
    const root = this.editableRootRef?.nativeElement;
    const span = root?.querySelector<HTMLElement>(`[data-field-id="${CSS.escape(id)}"]`);
    span?.remove();
    this.fields = this.fields.filter((f) => f.id !== id);
    if (this.activeFieldId === id) {
      this.activeFieldId = null;
      this.popupPosition = null;
    }
    this.syncDocumentFromEditor();
  }
}
